import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Hidden, IconButton, Tooltip } from '@mui/material';
import { Check, Close, DeleteForever, Edit, Menu as MenuIcon, Redo, Undo, Warning, } from '@mui/icons-material';
import Copy from '../images/blockly/copy.svg';
import { ScenarioEditor } from './scenario-editor';
import * as Blockly from 'blockly';
import { BlocklyError } from './utils/blockly-error';
import { connect } from 'react-redux';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { select_blockly_backpack_contents } from './backpack/selector';
import { createStructuredSelector } from 'reselect';
import { saveBackpackContent } from './backpack/actions';
import { isSmallUI } from 'shared/app/lib/choose-ui-type';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { FullScreenDialog } from '../dialogs/fullscreen';
import { mainB2C } from 'console/blockly/convertors/blockly-2-config/run';
import { mainX2B } from 'console/blockly/convertors/xml-2-blockly/main';
import { selectNullableSelectedDeviceId } from 'entities/device/lib';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { addAllPossibleCommands } from 'console/device-settings/z3k/utils/commands';
const ScenarioEditorDialogImpl = memo(function ScenarioEditorDialogImpl(props) {
    const { backpack_contents, scenario, device_id, onSaveBackpackContents, open, onRequestClose, } = props;
    const [xml, setXml] = useState(null);
    const [error, setError] = useState(null);
    const [showErrorPopup, closeErrorPopup, openErrorPopup] = useBooleanState(false);
    const [backpack, setBackpack] = useState(null);
    const mobileMode = useMemo(() => isSmallUI(), [isSmallUI]);
    const [toolboxVisible, onToolboxHidden, , toggleToolbox] = useBooleanState(!mobileMode);
    useEffect(() => {
        setError(null);
        setBackpack(null);
    }, [open]);
    const onSaveBackpack = useCallback(() => {
        try {
            if (device_id != null) {
                onSaveBackpackContents(backpack.getContents(), device_id);
            }
        }
        catch (e) {
            new BlocklyError("can't save backpack", 'Не смог сохранить рюкзачок');
        }
    }, [backpack, onSaveBackpackContents, device_id]);
    const validateAndDisplayError = useCallback((xml) => {
        try {
            setError(null);
            if (xml != null) {
                const dom_elements = Blockly.Xml.textToDom(xml);
                // const dom_elements = Blockly.utils.xml.textToDom(xml);
                const parsedData = mainX2B(dom_elements.children);
                const newConfig = mainB2C(addAllPossibleCommands(props.config), parsedData, scenario);
                return { ok: true, newConfig };
            }
            else {
                throw new BlocklyError('onSave xml == null', 'Поле с блоками пустое');
            }
        }
        catch (e) {
            if (e instanceof BlocklyError) {
                setError(e);
                // console.error(e.name, e.message);
                return { ok: false };
            }
            throw e;
        }
    }, [setError, mainX2B, mainB2C, addAllPossibleCommands, props.config, scenario]);
    const validateAndChangeConfig = useCallback((xml) => {
        const conversionResult = validateAndDisplayError(xml);
        if (conversionResult.ok) {
            props.onChangeConfig(conversionResult.newConfig);
        }
        return conversionResult.ok;
    }, [validateAndDisplayError, props.onChangeConfig]);
    const saveAndClose = useCallback(() => {
        if (validateAndChangeConfig(xml)) {
            //Сохраняем рюкзачок
            onSaveBackpack();
            //Закрываем диалог
            onRequestClose();
        }
    }, [xml, validateAndChangeConfig, onSaveBackpack, onRequestClose]);
    const handleClickDelete = useCallback(() => {
        const event = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            key: 'DELETE',
            keyCode: 46,
        });
        document.dispatchEvent(event);
    }, []);
    const handleClickCopy = useCallback(() => {
        const event = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            key: 'C',
            keyCode: 67,
            ctrlKey: true,
        });
        document.dispatchEvent(event);
    }, []);
    const handleClickPaste = useCallback(() => {
        const event = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            key: 'V',
            keyCode: 86,
            ctrlKey: true,
        });
        document.dispatchEvent(event);
    }, []);
    const handleClone = useCallback(() => {
        handleClickCopy();
        handleClickPaste();
    }, [handleClickCopy, handleClickPaste]);
    const handleClickUndo = useCallback(() => {
        const event = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            key: 'Z',
            keyCode: 90,
            ctrlKey: true,
        });
        document.dispatchEvent(event);
    }, []);
    const handleClickRedo = useCallback(() => {
        const event = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            key: 'Z',
            keyCode: 90,
            ctrlKey: true,
            shiftKey: true,
        });
        document.dispatchEvent(event);
    }, []);
    return (_jsxs(FullScreenDialog, { open: open, onClose: onRequestClose, toolbar: _jsxs(_Fragment, { children: [mobileMode ? (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: "medium", edge: "start", color: "inherit", onClick: toggleToolbox, children: _jsx(MenuIcon, {}) }), _jsx("div", { style: { flexGrow: 1 } })] })) : null, _jsxs(Box, { flexShrink: 0, children: [_jsx(Tooltip, { title: "\u0414\u0443\u0431\u043B\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u0431\u043B\u043E\u043A", children: _jsx(IconButton, { color: "inherit", onClick: handleClone, size: "large", children: _jsx(Copy, { width: 24, height: 24 }) }) }), _jsx(Tooltip, { title: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C", children: _jsx(IconButton, { color: "inherit", onClick: handleClickUndo, size: "large", children: _jsx(Undo, {}) }) }), mobileMode ? null : (_jsx(Tooltip, { title: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C", children: _jsx(IconButton, { color: "inherit", onClick: handleClickRedo, size: "large", children: _jsx(Redo, {}) }) })), _jsx(Tooltip, { title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0431\u043B\u043E\u043A", children: _jsx(IconButton, { color: "inherit", onClick: handleClickDelete, size: "large", children: _jsx(DeleteForever, {}) }) })] }), !mobileMode && error != null && (_jsx(Alert, { severity: "error", sx: { mr: 2 }, children: error.message })), _jsx(Box, { display: "flex", flexGrow: 1 }), _jsx(Tooltip, { title: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", children: _jsx(IconButton, { color: "inherit", onClick: onRequestClose, size: "large", children: _jsx(Close, {}) }) }), _jsx(Hidden, { smUp: true, children: error == null ? (_jsx(Tooltip, { title: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F", children: _jsx(IconButton, { edge: "end", onClick: saveAndClose, color: "success", size: "large", children: _jsx(Check, {}) }) })) : (_jsx(Tooltip, { title: "\u0415\u0441\u0442\u044C \u043E\u0448\u0438\u0431\u043A\u0438 \u0432 \u0441\u0446\u0435\u043D\u0430\u0440\u0438\u0438", children: _jsx(IconButton, { edge: "end", color: "secondary", onClick: openErrorPopup, size: "large", children: _jsx(Warning, {}) }) })) }), _jsx(Hidden, { mdDown: true, children: _jsx(Tooltip, { title: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F", children: _jsx(Button, { variant: "text", color: "inherit", onClick: saveAndClose, startIcon: _jsx(Check, {}), children: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C" }) }) })] }), children: [_jsx(ErrorBoundary, { children: _jsx(ScenarioEditor, { mobileMode: mobileMode, config: addAllPossibleCommands(props.config), scenario: scenario, backpack_contents: backpack_contents != null ? backpack_contents : [], toolboxVisible: toolboxVisible, onToolboxHidden: onToolboxHidden, onMeaningfulChangeXML: (newXml) => {
                        validateAndDisplayError(newXml);
                        setXml(newXml);
                    }, onSetBackpack: setBackpack }) }), _jsx(MessageBox, { open: showErrorPopup, onClose: closeErrorPopup, isError: true, title: "\u041D\u0435\u043A\u043E\u0440\u0440\u0435\u043A\u0442\u043D\u044B\u0439 \u0441\u0446\u0435\u043D\u0430\u0440\u0438\u0439", closeButtonTitle: "\u041F\u043E\u043D\u044F\u0442\u043D\u043E", children: error?.message })] }));
});
export const ScenarioEditorDialog = connect(createStructuredSelector({
    backpack_contents: select_blockly_backpack_contents,
    device_id: selectNullableSelectedDeviceId,
}), {
    onSaveBackpackContents: saveBackpackContent,
})(ScenarioEditorDialogImpl);
export const ScenarioEditorButton = memo(function ScenarioEditorButton({ scenario, config, onChangeConfig, }) {
    const [open, closeDialog, openDialog] = useBooleanState(false);
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "contained", color: "primary", onClick: openDialog, startIcon: _jsx(Edit, {}), children: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435 \u0441\u0446\u0435\u043D\u0430\u0440\u0438\u044F" }), _jsx(ErrorBoundary, { children: _jsx(ScenarioEditorDialog, { open: open, onRequestClose: closeDialog, scenario: scenario, config: config, onChangeConfig: onChangeConfig }) })] }));
});
